// app/components/product-review-modal.tsx
// ✅ WCAG AAA COMPLIANT - Product Review Modal Component for Catalyst
// Allows logged-in users to submit product reviews
// Follows Catalyst modal patterns: page scrolls, not modal content

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '~/hooks/use-focus-trap';

interface ProductReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: {
    id: string;
    treezId?: string;
    name: string;
    image?: string;
    brand?: string;
  };
  onReviewSubmitted?: () => void; // Callback when review is successfully submitted
}

// Utility to get a cookie by name
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = '; ' + document.cookie;
  const parts = value.split('; ' + name + '=');
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

// Form field configuration - only comment field (backend doesn't support title)
const formFields = [
  {
    id: 'description',
    name: 'description',
    label: 'Your Review',
    type: 'textarea',
    placeholder:
      'Describe the effects, quality, flavor, potency, and your overall experience with this product...',
    required: true,
  },
] as const;

export default function ProductReviewModal({
  isOpen,
  onClose,
  product,
  onReviewSubmitted,
}: ProductReviewModalProps) {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [formData, setFormData] = useState({
    description: '',
    termsAccepted: false,
  });
  const [userFirstName, setUserFirstName] = useState('');
  const [userLastName, setUserLastName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLTextAreaElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);

  // Use focus trap hook for WCAG AAA compliance
  const modalContainerRef = useFocusTrap(isOpen);

  // Delay rendering to prevent flash during hydration
  useEffect(() => {
    if (isOpen) {
      // Use requestAnimationFrame to ensure smooth rendering
      const rafId = requestAnimationFrame(() => {
        setShouldRender(true);
      });
      return () => cancelAnimationFrame(rafId);
    } else {
      // Delay unmounting to allow exit animations
      const timeout = setTimeout(() => {
        setShouldRender(false);
      }, 300); // Match your modal animation duration
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  // Lock body scrolling when modal is open - but only if not nested in another modal
  // Match Embarc's nested modal handling pattern
  useEffect(() => {
    if (isOpen) {
      // Store the last focused element before opening modal
      lastFocusedElementRef.current = document.activeElement as HTMLElement;

      // Check if we're nested inside the global product modal
      const isNested =
        !!document.querySelector('#product-modal') || !!document.querySelector('#modal-wrapper');

      let originalStyles: any = {};

      // Only manage body styles if we're NOT nested
      if (!isNested) {
        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
        originalStyles = {
          overflow: document.body.style.overflow,
          paddingRight: document.body.style.paddingRight,
          documentOverflow: document.documentElement.style.overflow,
          touchAction: document.body.style.touchAction,
        };

        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollBarWidth}px`;
        document.documentElement.style.overflow = 'hidden';
        document.body.style.touchAction = 'manipulation';
      } else {
        // If nested, store the current parent modal's scroll lock state
        // so we can restore it when closing
        originalStyles = {
          overflow: document.body.style.overflow,
          paddingRight: document.body.style.paddingRight,
          documentOverflow: document.documentElement.style.overflow,
          touchAction: document.body.style.touchAction,
          backgroundColor: document.body.style.backgroundColor,
          isNested: true,
        };
      }

      // Always handle touch events for this modal - allow scrolling within modal
      const preventTouchMove = (e: TouchEvent) => {
        const target = e.target as Element;
        const backdrop = backdropRef.current;

        // Allow scrolling if touch is inside the modal content
        if (backdrop && backdrop.contains(target)) {
          e.stopPropagation();
          return;
        }

        const isInsideAnyDialog =
          target.closest('[role="dialog"]') ||
          target.closest('[aria-modal="true"]') ||
          target.matches('button, input, textarea, select, a[href]') ||
          target.closest('button, input, textarea, select, a[href]');

        if (isInsideAnyDialog) {
          return;
        }

        // Prevent scrolling outside modal
        e.preventDefault();
      };

      document.addEventListener('touchmove', preventTouchMove, { passive: false, capture: true });

      return () => {
        // Always remove touch event listener
        document.removeEventListener('touchmove', preventTouchMove, { capture: true });

        // Check again at cleanup time to be safe
        const stillNested =
          !!document.querySelector('#product-modal') || !!document.querySelector('#modal-wrapper');

        // Handle body styles restoration
        if (!isNested && !stillNested) {
          // We set the styles and we're not nested, so restore them
          document.body.style.overflow = originalStyles.overflow || '';
          document.body.style.paddingRight = originalStyles.paddingRight || '';
          document.documentElement.style.overflow = originalStyles.documentOverflow || '';
          document.body.style.touchAction = originalStyles.touchAction || '';
        } else {
          // We're nested - don't touch scroll lock styles at all
          // The parent modal's useFocusTrap will maintain scroll lock
          // Just restore paddingRight if we changed it (we didn't when nested, but check anyway)
          if (originalStyles.paddingRight) {
            document.body.style.paddingRight = originalStyles.paddingRight;
          }
        }

        // Restore focus when modal closes - but only if not nested (let parent handle focus)
        if (!isNested && !stillNested && lastFocusedElementRef.current) {
          // Only restore focus if the current focused element is still the modal or its children
          const currentFocus = document.activeElement;
          if (
            !currentFocus ||
            currentFocus === document.body ||
            modalRef.current?.contains(currentFocus)
          ) {
            lastFocusedElementRef.current.focus();
          }
        }
      };
    } else {
      // Modal is closed - force cleanup of any lingering scroll locks
      // This handles cases where cleanup didn't run properly (e.g., back button, URL sync)
      const isNested =
        !!document.querySelector('#product-modal') || !!document.querySelector('#modal-wrapper');
      
      if (!isNested) {
        // Only restore if we're not inside another modal
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.documentElement.style.overflow = '';
        document.body.style.touchAction = '';
      }
    }
  }, [isOpen]);

  // Load user data from cookies when modal opens (optional - only for display)
  useEffect(() => {
    if (typeof window !== 'undefined' && isOpen) {
      const firstName = getCookie('first_name') || '';
      const lastName = getCookie('last_name') || '';
      const email = getCookie('email') || '';

      // Decode URL-encoded values (cookies are set with encodeURIComponent)
      if (firstName) {
        try {
          setUserFirstName(decodeURIComponent(firstName));
        } catch (e) {
          setUserFirstName(firstName);
        }
      } else {
        setUserFirstName('');
      }
      if (lastName) {
        try {
          setUserLastName(decodeURIComponent(lastName));
        } catch (e) {
          setUserLastName(lastName);
        }
      } else {
        setUserLastName('');
      }
      if (email) {
        try {
          setUserEmail(decodeURIComponent(email));
        } catch (e) {
          setUserEmail(email);
        }
      } else {
        setUserEmail('');
      }
    }
  }, [isOpen]); // Reload when modal opens

  // Focus management - Set initial focus to first input or close button
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        if (firstInputRef.current) {
          firstInputRef.current.focus();
        } else if (closeButtonRef.current) {
          closeButtonRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        description: '',
        termsAccepted: false,
      });
      setRating(0);
      setHoveredRating(0);
      setErrors({});
      setSubmitSuccess(false);
    }
  }, [isOpen]);

  // Close modal on Escape key (handled by focus trap, but also handle here for redundancy)
  useEffect(() => {
    const handleCloseEvent = () => {
      onClose();
    };
    if (isOpen) {
      document.addEventListener('close-focus-trap', handleCloseEvent);
    }
    return () => {
      document.removeEventListener('close-focus-trap', handleCloseEvent);
    };
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Handle checkbox change
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, termsAccepted: e.target.checked }));
    if (errors.termsAccepted) {
      setErrors((prev) => ({ ...prev, termsAccepted: '' }));
    }
  };

  // Validate form
  const validateForm = (): { isValid: boolean; errors: Record<string, string> } => {
    const newErrors: Record<string, string> = {};

    if (!formData.description.trim()) {
      newErrors.description = 'Review comment is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Review comment must be at least 10 characters long';
    }
    if (rating === 0) {
      newErrors.rating = 'Rating is required';
    }
    // Don't require user data fields - only catalystUserId is required (checked in handleSubmit)
    if (!formData.termsAccepted) {
      newErrors.termsAccepted = 'You must accept the terms to submit a review';
    }

    setErrors(newErrors);

    return {
      isValid: Object.keys(newErrors).length === 0,
      errors: newErrors,
    };
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateForm();
    if (!validation.isValid) {
      // Focus first error field
      const firstErrorId = Object.keys(validation.errors)[0];
      if (firstErrorId) {
        setTimeout(() => {
          const errorElement = document.getElementById(firstErrorId);
          if (errorElement) {
            errorElement.focus();
          }
        }, 100);
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const userId = getCookie('catalystUserId');
      if (!userId) {
        throw new Error('User not logged in');
      }

      // Submit review via API route
      const response = await fetch('/api/reviews', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          actionType: 'ADD',
          product_id: product.id,
          rating,
          comment: formData.description,
          user_id: userId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit review');
      }

      setSubmitSuccess(true);

      // Announce success to screen readers
      const liveRegion = document.createElement('div');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.setAttribute('style', 'position: absolute; left: -9999px; top: -9999px;');
      document.body.appendChild(liveRegion);
      liveRegion.textContent = 'Thank you for your review! We appreciate your feedback.';
      setTimeout(() => {
        liveRegion.remove();
      }, 5000);

      // Call onReviewSubmitted callback to refresh reviews in parent component
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }

      // Focus the close button in the success message for accessibility
      setTimeout(() => {
        const closeButton = document.querySelector('[data-success-close-button]') as HTMLElement;
        if (closeButton) {
          closeButton.focus();
        }
      }, 100);
    } catch (error) {
      console.error('Error submitting review:', error);
      setErrors({
        submit:
          error instanceof Error ? error.message : 'Failed to submit review. Please try again.',
      });
      // Focus submit button to announce error
      const submitButton = document.querySelector('button[type="submit"]') as HTMLElement;
      if (submitButton) {
        submitButton.focus();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Safety guard — don't render the review form if user is not logged in
  const userId = getCookie('catalystUserId');
  if ((!isOpen && !shouldRender) || !userId) return null;

  const modalContent = (
    <div
      ref={backdropRef}
      className={`fixed inset-0 z-[180] flex items-start justify-center overflow-y-auto bg-black/50 p-4 transition-opacity duration-300 ${
        isOpen && shouldRender ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-modal-title"
      aria-describedby="review-modal-description"
      style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
    >
      <div
        ref={modalContainerRef}
        className={`relative z-[181] my-auto w-full max-w-[730px] rounded-xl bg-white shadow-xl transition-all duration-300 ${
          isOpen && shouldRender ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Container - Page scrolls, not modal content */}
        <div className="flex flex-col p-4 md:p-8">
          {/* Header */}
          <div className="relative mb-4 flex flex-col gap-4">
            {/* Close Button */}
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="absolute top-0 right-0 flex items-center gap-1 text-sm text-[#94A3B8] hover:text-gray-600 focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
              aria-label="Close review modal"
            >
              <span className="font-gothic-a1">Close</span>
              <svg
                className="h-3.5 w-3.5 rounded-sm border border-[#94A3B8]"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                  stroke="#94A3B8"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            {/* Title */}
            <h2
              id="review-modal-title"
              className="font-karla text-2xl leading-7 font-bold text-[#0F172A] md:text-[32px] md:leading-7"
            >
              Leave a Review
            </h2>

            {/* Description */}
            <p
              id="review-modal-description"
              className="font-gothic-a1 text-base leading-6 font-light text-black"
            >
              Share your experience with this product. Help other customers by rating the effects,
              quality, flavor, and potency.
            </p>

            {/* Required Field Indicator */}
            <div className="flex items-start gap-0.5">
              <span className="font-gothic-a1 text-xs leading-5 font-normal text-red-500">*</span>
              <span className="font-gothic-a1 text-xs leading-5 font-light text-[#64748B]">
                Indicates a required field or selection.
              </span>
            </div>
          </div>

          {/* Product Info */}
          <div className="mb-6 border-b border-[#CBD5E1] pb-6">
            <div className="flex items-center gap-4">
              {product.image && (
                <img
                  src={product.image}
                  alt=""
                  className="h-16 w-16 rounded-lg border border-[#CBD5E1] bg-white object-scale-down"
                  role="presentation"
                  aria-hidden="true"
                />
              )}
              <div>
                <h3 className="font-karla text-lg font-bold text-[#0F172A]">{product.name}</h3>
                {product.brand && (
                  <p className="font-gothic-a1 text-sm text-[#64748B]">{product.brand}</p>
                )}
              </div>
            </div>
          </div>

          {/* Success Message - Show only when submitted */}
          {submitSuccess && (
            <div
              className="mb-4 rounded-lg border border-[#45A848] bg-[#F0FDF4] p-4"
              role="status"
              aria-live="polite"
            >
              <div className="flex items-start justify-between gap-4">
                <p className="font-gothic-a1 flex-1 text-base font-medium text-[#166534]">
                  Thank you for your review. Your feedback helps us improve.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    setSubmitSuccess(false);
                  }}
                  className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-sm transition-colors hover:bg-[#45A848]/20 focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
                  aria-label="Close review modal"
                  data-success-close-button
                >
                  <svg
                    className="h-4 w-4 text-[#166534]"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Form - Hide when success is shown */}
          {!submitSuccess && (
            <form onSubmit={handleSubmit} className="flex flex-col gap-5.5" noValidate>
              {/* Review Comment Field */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="description"
                  className="font-gothic-a1 flex items-start gap-1 text-base leading-5 font-medium text-black"
                >
                  <span>Your Review</span>
                  <span className="font-gothic-a1 text-xs leading-5 font-normal text-red-500">
                    *
                  </span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  ref={firstInputRef}
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Describe the effects, quality, flavor, potency, and your overall experience with this product..."
                  className="font-karla resize-vertical w-full rounded-lg border border-[#CBD5E1] px-6 py-4 text-sm leading-6 font-normal tracking-[-0.02em] text-black placeholder:text-[#64748B] focus:border-[#264B9A] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
                  aria-required="true"
                  aria-invalid={!!errors.description}
                  aria-describedby={errors.description ? 'description-error' : undefined}
                />
                {errors.description && (
                  <span
                    id="description-error"
                    className="font-gothic-a1 text-sm text-red-500"
                    role="alert"
                  >
                    {errors.description}
                  </span>
                )}
              </div>

              {/* Rating Field - Moved between Description and First Name */}
              <div className="flex flex-col gap-2">
                <div className="font-gothic-a1 flex items-start gap-1 text-base leading-5 font-medium text-black">
                  <span id="rating-label">Overall Experience with this Product</span>
                  <span className="font-gothic-a1 text-xs leading-5 font-normal text-red-500">
                    *
                  </span>
                </div>

                {/* Star Rating */}
                <div
                  className="flex items-center gap-0.5"
                  role="radiogroup"
                  aria-labelledby="rating-label"
                  aria-required="true"
                  aria-invalid={!!errors.rating}
                  aria-describedby={errors.rating ? 'rating-error' : undefined}
                >
                  {[1, 2, 3, 4, 5].map((starValue) => (
                    <button
                      key={starValue}
                      type="button"
                      onClick={() => setRating(starValue)}
                      onMouseEnter={() => setHoveredRating(starValue)}
                      onMouseLeave={() => setHoveredRating(0)}
                      className="h-6 w-6 rounded-sm transition-opacity focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
                      aria-label={`Rate ${starValue} out of 5 stars`}
                      aria-pressed={starValue <= rating}
                      role="radio"
                      aria-checked={starValue <= rating}
                    >
                      <svg
                        width="16"
                        height="15"
                        viewBox="0 0 16 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-6 w-6 transition-opacity ${
                          starValue <= (hoveredRating || rating) ? 'opacity-100' : 'opacity-30'
                        }`}
                        aria-hidden="true"
                      >
                        <path
                          d="M7.14968 0.334803C7.30597 -0.111532 7.93719 -0.111532 8.09349 0.334803L9.63376 4.73339C9.7026 4.92997 9.88609 5.06329 10.0943 5.06801L14.7536 5.17366C15.2264 5.18438 15.4214 5.7847 15.0453 6.07127L11.3379 8.8954C11.1722 9.02162 11.1021 9.23733 11.162 9.43683L12.5013 13.9007C12.6372 14.3537 12.1266 14.7247 11.7378 14.4555L7.90623 11.8023C7.73499 11.6837 7.50818 11.6837 7.33694 11.8023L3.5054 14.4555C3.11661 14.7247 2.60594 14.3537 2.74185 13.9007L4.08116 9.43683C4.14102 9.23733 4.07093 9.02162 3.90524 8.8954L0.19791 6.07127C-0.178282 5.7847 0.0167747 5.18438 0.489562 5.17366L5.14883 5.06801C5.35707 5.06329 5.54056 4.92997 5.6094 4.73339L7.14968 0.334803Z"
                          fill={starValue <= (hoveredRating || rating) ? '#FBBF24' : '#94A3B8'}
                        />
                      </svg>
                      <span className="sr-only">
                        {starValue} star{starValue !== 1 ? 's' : ''}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Rating Error */}
                {errors.rating && (
                  <span
                    id="rating-error"
                    className="font-gothic-a1 text-sm text-red-500"
                    role="alert"
                  >
                    {errors.rating}
                  </span>
                )}
              </div>

              {/* Terms and Conditions */}
              <div className="flex items-start gap-2 py-1">
                <input
                  id="terms"
                  name="termsAccepted"
                  type="checkbox"
                  checked={formData.termsAccepted}
                  onChange={handleCheckboxChange}
                  className="mt-0.5 h-4 w-4 rounded border border-[#CBD5E1] text-[#45A848] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2"
                  aria-required="true"
                  aria-invalid={!!errors.termsAccepted}
                  aria-describedby={errors.termsAccepted ? 'terms-error' : 'terms-description'}
                />
                <label
                  htmlFor="terms"
                  className="font-gothic-a1 flex-1 text-sm leading-5 font-light text-[#64748B]"
                >
                  <span id="terms-description">
                    I acknowledge that I have purchased or used this product and agree to the{' '}
                    <a
                      href="/terms-of-use"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#264B9A] underline hover:text-[#1a3a7a] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
                    >
                      Terms of Use
                    </a>
                    .
                  </span>
                </label>
              </div>

              {/* Terms Error */}
              {errors.termsAccepted && (
                <span id="terms-error" className="font-gothic-a1 text-sm text-red-500" role="alert">
                  {errors.termsAccepted}
                </span>
              )}

              {/* Submit Error */}
              {errors.submit && (
                <div
                  className="font-gothic-a1 rounded-lg bg-red-50 p-3 text-sm text-red-600"
                  role="alert"
                  aria-live="assertive"
                >
                  {errors.submit}
                </div>
              )}

              {/* Hidden fields to send user data */}
              {userFirstName && userLastName && (
                <input type="hidden" name="name" value={`${userFirstName} ${userLastName}`} />
              )}
              {userEmail && <input type="hidden" name="email" value={userEmail} />}

              {/* Logged-in user indicator - Always show since only logged-in users can use reviews */}
              {(userFirstName || userEmail) && (
                <div className="mb-4 rounded-lg border border-[#45A848] bg-[#F0FDF4] p-4">
                  <div className="flex items-center gap-2">
                    <svg
                      className="h-5 w-5 text-[#45A848]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="font-gothic-a1 text-sm leading-5 font-light text-[#166534]">
                      <span className="font-medium">
                        Signed in as {userFirstName || userEmail?.split('@')[0] || 'User'}
                      </span>{' '}
                      - Your account details will be used automatically
                    </p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || submitSuccess}
                className="font-gothic-a1 flex h-11 w-full items-center justify-center rounded-lg bg-[#45A848] px-6 py-4 text-lg leading-6 font-medium text-white transition-colors hover:bg-[#3a8a3d] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                aria-busy={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );

  return typeof window !== 'undefined' ? createPortal(modalContent, document.body) : null;
}
